import { isToday, isYesterday, format } from 'date-fns';
import type { Attachment, MessageResponse } from 'stream-chat';
import type { ClinicalMediaType } from '@/api/apis/case';
import { MediaType, type MediaDateGroup, type MediaItem, type VirtualRow } from './types';
import { MEDIA_TILES_PER_ROW } from './constants';

export function chunkIntoRows(items: MediaItem[], tilesPerRow: number = MEDIA_TILES_PER_ROW): MediaItem[][] {
  const rows: MediaItem[][] = [];

  items.forEach((item, index) => {
    const rowIndex = Math.floor(index / tilesPerRow);
    if (!rows[rowIndex]) {
      rows[rowIndex] = [];
    }
    rows[rowIndex].push(item);
  });

  return rows;
}

export function formatMediaDate(date: Date): string {
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  return format(date, 'EEE d, MMM yyyy');
}

export function getDateKey(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

export function extractMediaFromMessage(message: MessageResponse): MediaItem[] {
  if (!message.attachments?.length) return [];

  const createdAt = message.created_at ? new Date(message.created_at) : new Date();
  const items: MediaItem[] = [];

  message.attachments.forEach((att: Attachment, index: number) => {
    if (att.type === 'image' || att.type === 'giphy') {
      const url = att.image_url || att.thumb_url || att.asset_url;
      if (url) {
        items.push({
          id: `${message.id}-${index}`,
          name: att.fallback || '',
          type: 'image',
          url,
          thumbUrl: att.thumb_url,
          createdAt,
          messageId: message.id,
        });
      }
    } else if (att.type === 'video') {
      const url = att.thumb_url || att.asset_url;
      items.push({
        id: `${message.id}-${index}`,
        name: att.fallback || '',
        type: 'video',
        url: att.asset_url ?? url ?? '',
        thumbUrl: url,
        createdAt,
        messageId: message.id,
      });
    }
  });

  return items;
}

export function mediaItemsToClinicalMedia(items: MediaItem[]): ClinicalMediaType[] {
  return items.map((item) => ({
    file: {
      fileId: item.id,
      fileName: item.name,
      path: item.url,
      mediaType: item.type === 'image' ? MediaType.Photo : MediaType.Video,
      ...(item.thumbUrl && {
        preview: {
          fileId: item.id,
          fileName: item.id,
          path: item.thumbUrl,
          mediaType: MediaType.Photo,
        },
      }),
    },
  }));
}

export function groupByDate(items: MediaItem[]): MediaDateGroup[] {
  const groupMap = items.reduce<Map<string, MediaDateGroup>>((map, item) => {
    const key = getDateKey(item.createdAt);
    if (!map.has(key)) {
      map.set(key, {
        dateKey: key,
        dateLabel: formatMediaDate(item.createdAt),
        items: [],
      });
    }
    map.get(key)!.items.push(item);
    return map;
  }, new Map());

  return Array.from(groupMap.values());
}

export const buildVirtualRows = (groups: MediaDateGroup[], tilesPerRow: number): VirtualRow[] => {
  return groups.flatMap((group) => [
    { kind: 'header' as const, dateLabel: group.dateLabel, dateKey: group.dateKey },
    ...chunkIntoRows(group.items, tilesPerRow).map((items) => ({ kind: 'tiles' as const, items })),
  ]);
};

export function getGroupItemsByMediaId(groups: MediaDateGroup[], selectedId: string): MediaItem[] {
  if (!selectedId) return [];
  const group = groups.find((g) => g.items.some((item) => item.id === selectedId));
  return group ? group.items : [];
}
